import { useRecoilValue } from "recoil";
import styled from "styled-components";

import CheckBox from "components/atoms/input/CheckBox";
import COLORS from "components/atoms/colors";
import { H3, H4, TextSm } from "components/atoms/text";
import SquareMeasure from "components/layouts/Header/ModalPalette/Palette/SquareMeasure";
import Image from "components/organisms/Image";

import Scene from "types/Scene";
import PinIdInfo from "types/PinIdInfo";

import authAtom from "stores/auth";

interface Props {
  scenes: Scene[];
  projectId: number;
  pinIdInfo: PinIdInfo[];
  onChangeEachPin: (params: { checked: boolean; pinId: number }) => void;
  onChangeScenePins: (params: {
    checked: boolean;
    idInfos: PinIdInfo[];
  }) => void;
  onChageEachMeasure: (params: {
    pinId: number;
    squareMeasure: string;
  }) => void;
  isNoMeasure: boolean;
}

// 팔레트 - 갤러리형
const Gallery = ({
  scenes,
  projectId,
  pinIdInfo,
  onChangeEachPin,
  onChangeScenePins,
  onChageEachMeasure,
  isNoMeasure,
}: Props) => {
  const auth = useRecoilValue(authAtom);

  return (
    <>
      {scenes.map((scene, i) => {
        const { id, pins } = scene;

        const scenePinIds = pins.map((pin) => ({
          pinId: pin.id,
          squareMeasure: "",
        }));

        return (
          <div key={id} className={`mb-15 scene${i + 1}`}>
            <TitleWrap>
              <H3>SCENE {i + 1}</H3>
              {pins.length > 0 && (
                <CheckBox
                  className="my-5"
                  checked={pins.every(
                    (pin) => !!pinIdInfo.find((p) => p.pinId === pin.id)
                  )}
                  onChange={(e) =>
                    onChangeScenePins({
                      checked: e.target.checked,
                      idInfos: scenePinIds,
                    })
                  }
                />
              )}
            </TitleWrap>

            <div className="grid grid-cols-4 gap-15 mt-5">
              {pins?.map((pin) => {
                const { name, category, image, subCategory, isShowPrice } =
                  pin.productOption.product;

                const noMeasureItems = pinIdInfo.filter(
                  (p) => !p.squareMeasure
                );

                const emptyMeasure =
                  isNoMeasure &&
                  !!noMeasureItems.find((p) => p.pinId === pin.id);

                return (
                  <div key={pin.id} className="relative">
                    <ImageWrap>
                      <div className="absolute left-3 top-3">
                        <CheckBox
                          checked={!!pinIdInfo.find((p) => p.pinId === pin.id)}
                          onChange={(e) =>
                            onChangeEachPin({
                              checked: e.target.checked,
                              pinId: pin.id,
                            })
                          }
                        />
                      </div>
                      <Image src={image} />
                    </ImageWrap>
                    <H4>{name}</H4>
                    <InfoWrap>
                      <InfoTitle>구분</InfoTitle>
                      <TextSm>{category?.name}</TextSm>
                    </InfoWrap>
                    <InfoWrap>
                      <InfoTitle>종류</InfoTitle>
                      <TextSm>{subCategory}</TextSm>
                    </InfoWrap>
                    <InfoWrap>
                      <InfoTitle>
                        m²<Essential>*</Essential>
                      </InfoTitle>
                      <SquareMeasure
                        placeholder="적용면적입력"
                        className="text-right !w-[62px] !px-0"
                        onChageEachMeasure={(squareMeasure) =>
                          onChageEachMeasure({ pinId: pin.id, squareMeasure })
                        }
                        squareMeasure={
                          pinIdInfo.find((p) => p.pinId === pin.id)
                            ?.squareMeasure
                        }
                        focus={emptyMeasure}
                      />
                    </InfoWrap>
                    <InfoWrap>
                      <InfoTitle>단가(₩)</InfoTitle>
                      <TextSm bold className="!text-black-100%">
                        {(() => {
                          if (auth.type === "user" || !isShowPrice) {
                            return "가격문의";
                          }
                          return `￦${pin.productOption.price?.toLocaleString()}`;
                        })()}
                      </TextSm>
                    </InfoWrap>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
};

const ImageWrap = styled.div`
  margin-bottom: 20px;
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 4px;
  overflow: hidden;
`;

const InfoWrap = styled.div`
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
  gap: 10px;

  p {
    color: ${COLORS.black["60%"]};
  }
`;

const InfoTitle = styled(TextSm)`
  flex: none;
  width: 50px;
`;

const TitleWrap = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  &::after {
    content: "";
    display: block;
    flex: 1;
    height: 1px;
    background: #d2d2d2;
  }
`;

const Essential = styled.span`
  vertical-align: top;
  color: ${COLORS.red};
`;

export default Gallery;
